/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC, useContext, useEffect, useState } from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { PrivateRoutes } from './PrivateRoutes'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { Logout, AuthPage } from '../modules/auth'
import { App } from '../App'
import { I18nContext } from '../../_metronic/i18n/Metronici18n'
import VerifyEmail from '../modules/auth/components/VerifyEmail'
import { toast } from 'sonner'
import OfflinePage from '../pages/offline/Offline'
import { useFeature } from '../../Utils/featureFlagEngine'
import { Waf } from '../../app/pages/waf'
import { useAuthStore } from '../../store/authStore'
import { useIntl } from 'react-intl'

const { PUBLIC_URL } = process.env

const AppRoutes: FC = () => {
  const { getMainRoute } = useFeature()
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isAuthorized = useAuthStore((state) => state.user)
  const dir = useContext(I18nContext)?.lang.dir || 'rtl'
  const intl = useIntl()
  useEffect(() => {
    const html = document.getElementsByTagName('html')[0]
    html.setAttribute('dir', dir)
  }, [dir])

  useEffect(() => {
    // Only offer to install when the app is not already installed.
    const isInstalled =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true
    if (isInstalled) return

    let timeoutId: ReturnType<typeof setTimeout>
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault()
      timeoutId = setTimeout(() => {
        toast.info(intl.formatMessage({ id: 'Install our app for faster access and a better experience.' }), {
          action: {
            label: intl.formatMessage({ id: 'Install' }),
            onClick: () => e.prompt(),
          },
        })
      }, 5000)
    }

    // Add event listener
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Cleanup listener
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      clearTimeout(timeoutId)
    }
  }, [])


  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);
  return isOnline ? (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>

        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path='logout' element={<Logout />} />
          {isAuthorized ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to={getMainRoute() || ""} />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='activate/:uid/:token' element={<VerifyEmail />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  ) : <OfflinePage />
}

export { AppRoutes }
